import { useQuery } from '@tanstack/react-query';
import { analyticsApi, staffApi, tablesApi, ordersApi, complianceApi, ComplianceAlert, inventoryApi, invoicesApi, reportsApi, restaurantApi, wholesaleApi } from '../services/api';
import { useState } from 'react';
import StatCard from '../components/StatCard';
import SalesChart from '../components/SalesChart';
import MakerDashboard from '../components/MakerDashboard';
import OperatingDashboard from '../components/OperatingDashboard';
import { isCpgSettings } from '../utils/cpg';
import {
  DollarSign,
  ShoppingCart,
  Users,
  Clock,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Shield,
  FileWarning,
  Calendar,
  Truck,
  FileText,
  PieChart,
  Package,
} from 'lucide-react';
import HelpButton from '../components/HelpButton';
import ReadyForServiceCard from '../components/ReadyForServiceCard';
import { format } from 'date-fns';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

export default function DashboardPage() {
  // Makers / wholesale CPGs get a different home — the restaurant metrics
  // (covers, POS revenue, tables) are all zero and meaningless for them.
  const currentRestaurantId = (() => {
    try { return JSON.parse(localStorage.getItem('manager_current_restaurant') || 'null')?.id || null; } catch { return null; }
  })();
  // NOTE: distinct query key from Layout's ['restaurant-modules', …]. That key
  // caches the MODULES OBJECT; this one caches the FULL RESTAURANT. Sharing a key
  // with different shapes made whichever fetched first poison the other — sidebar
  // showing restaurant modules while the dashboard showed the maker view (or vice
  // versa), flip-flopping with cache refreshes.
  const { data: restaurant, isPending: modulesPending } = useQuery({
    queryKey: ['restaurant-current', currentRestaurantId],
    queryFn: async () => (await restaurantApi.getCurrent()).data?.data || null,
    staleTime: 30 * 1000, refetchOnWindowFocus: true,
    retry: 3,
  });
  const modules = (restaurant?.settings?.modules || {}) as Record<string, boolean>;
  const isMaker = modules.production === true;
  // Preserve the forecast-led home for brands with an existing forecast.
  const isOperating = isCpgSettings(restaurant?.settings) && !restaurant?.settings?.channelForecast && !restaurant?.settings?.overviewSeries;
  const showRestaurantMetrics = !modulesPending && !isMaker && !isOperating;

  // Fetch dashboard data — only once we KNOW this isn't a maker account, so the
  // restaurant dashboard never flashes (or sticks) for a wholesale/CPG business.
  const { data: dashboardData, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const response = await analyticsApi.getDashboard();
      return response.data?.data || response.data;
    },
    refetchInterval: 60000,
    enabled: showRestaurantMetrics,
  });

  // Fetch staff on clock
  const { data: clockedInStaff } = useQuery({
    queryKey: ['clocked-in-staff'],
    queryFn: async () => {
      const response = await staffApi.getClockedIn();
      return response.data?.data || response.data;
    },
    refetchInterval: 60000,
    enabled: showRestaurantMetrics,
  });

  // Fetch table stats
  const { data: tableStats } = useQuery({
    queryKey: ['table-stats'],
    queryFn: async () => {
      const response = await tablesApi.getStats();
      return response.data?.data || response.data;
    },
    refetchInterval: 30000,
    enabled: showRestaurantMetrics,
  });

  // Fetch recent orders
  const { data: recentOrders } = useQuery({
    queryKey: ['recent-orders'],
    queryFn: async () => {
      const response = await ordersApi.getRecent(10);
      return response.data?.data || response.data;
    },
    refetchInterval: 30000,
    enabled: showRestaurantMetrics,
  });

  // Fetch compliance stats and alerts
  const { data: complianceStats } = useQuery({
    queryKey: ['compliance-stats'],
    queryFn: async () => {
      const response = await complianceApi.getDashboardStats();
      return response.data?.data || response.data;
    },
    enabled: showRestaurantMetrics,
    refetchInterval: 300000, // 5 minutes
  });

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  // Default empty data (zeros for new restaurants)
  const emptyDashboard = {
    todayRevenue: 0,
    todayOrders: 0,
    averageTicket: 0,
    revenueTrend: 0,
    ordersTrend: 0,
    ticketTrend: 0,
    hourlySales: [],
  };

  const emptyTables = {
    total: 0,
    available: 0,
    occupied: 0,
    reserved: 0,
  };

  // Procurement widgets
  const { data: orderSuggestions } = useQuery({
    queryKey: ['order-suggestions-summary'],
    queryFn: async () => {
      const response = await inventoryApi.getOrderSuggestions();
      return response.data?.data || response.data;
    },
    enabled: showRestaurantMetrics,
    refetchInterval: 120000,
  });

  const { data: pendingPurchaseOrders } = useQuery({
    queryKey: ['pending-purchase-orders'],
    queryFn: async () => {
      const response = await inventoryApi.getPurchaseOrders();
      const list = response.data?.data || response.data;
      return Array.isArray(list) ? list : [];
    },
    enabled: showRestaurantMetrics,
    refetchInterval: 120000,
  });

  const { data: invoiceDiscrepancies } = useQuery({
    queryKey: ['invoice-discrepancies-summary'],
    queryFn: async () => {
      const response = await invoicesApi.getDiscrepancies();
      return response.data?.data || response.data;
    },
    enabled: showRestaurantMetrics,
    refetchInterval: 120000,
  });

  const { data: foodCostWeek } = useQuery({
    queryKey: ['food-cost-week'],
    queryFn: async () => {
      const response = await reportsApi.getFoodCost({ period: 'week' });
      return response.data?.data || response.data;
    },
    enabled: showRestaurantMetrics,
    refetchInterval: 300000,
  });

  const procurement = {
    itemsToOrder: orderSuggestions?.summary?.total ?? 0,
    criticalToOrder: orderSuggestions?.summary?.critical ?? 0,
    pendingDeliveries: Array.isArray(pendingPurchaseOrders)
      ? pendingPurchaseOrders.filter((po: any) => po.status !== 'received').length
      : 0,
    invoiceProblems: invoiceDiscrepancies?.summary?.count ?? 0,
    invoiceVarianceCents: invoiceDiscrepancies?.summary?.totalVarianceCents ?? 0,
    foodCostPercent: foodCostWeek?.actualFoodCostPercent ?? null,
  };

  // Use real data or empty defaults (no fake demo data)
  const data = {
    todayRevenue: dashboardData?.todayRevenue ?? emptyDashboard.todayRevenue,
    todayOrders: dashboardData?.todayOrders ?? emptyDashboard.todayOrders,
    averageTicket: dashboardData?.averageTicket ?? emptyDashboard.averageTicket,
    revenueTrend: dashboardData?.revenueTrend ?? emptyDashboard.revenueTrend,
    ordersTrend: dashboardData?.ordersTrend ?? emptyDashboard.ordersTrend,
    ticketTrend: dashboardData?.ticketTrend ?? emptyDashboard.ticketTrend,
    hourlySales: dashboardData?.hourlySales ?? emptyDashboard.hourlySales,
  };
  const tables = {
    total: tableStats?.total ?? emptyTables.total,
    available: tableStats?.available ?? emptyTables.available,
    occupied: tableStats?.occupied ?? emptyTables.occupied,
    reserved: tableStats?.reserved ?? emptyTables.reserved,
  };
  const staff = Array.isArray(clockedInStaff) ? clockedInStaff : [];
  const orders = Array.isArray(recentOrders) ? recentOrders : [];

  // Maker/CPG home instead of the restaurant metrics (placed after all hooks).
  // Hold on a quiet loader until we know which home this account gets — never
  // show a maker the restaurant zeros (tables, staff, tickets) even for a frame.
  if (modulesPending) {
    return <div className="p-6 flex items-center justify-center min-h-[40vh]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>;
  }
  if (isOperating) return <OperatingDashboard key={currentRestaurantId} workspaceId={currentRestaurantId} restaurantName={restaurant?.name} />;
  if (isMaker) return <MakerDashboard restaurantName={restaurant?.name} />;

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <ConnectToolsNudge />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-sm text-slate-500">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
          </div>
          <HelpButton featureId="dashboard" />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-500">Last updated:</span>
          <span className="font-medium">
            {format(dataUpdatedAt ? new Date(dataUpdatedAt) : new Date(), 'h:mm a')}
          </span>
        </div>
      </div>

      {/* Setup checklist — hides itself once the restaurant is service-ready */}
      <ReadyForServiceCard />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        <StatCard
          title="Today's Revenue"
          value={formatCurrency(data.todayRevenue)}
          change={data.revenueTrend}
          changeLabel="vs yesterday"
          icon={<DollarSign className="w-6 h-6 text-primary-600" />}
          iconBg="bg-primary-100"
        />
        <StatCard
          title="Orders"
          value={data.todayOrders}
          change={data.ordersTrend}
          changeLabel="vs yesterday"
          icon={<ShoppingCart className="w-6 h-6 text-success-600" />}
          iconBg="bg-success-100"
        />
        <StatCard
          title="Avg Ticket"
          value={formatCurrency(data.averageTicket)}
          change={data.ticketTrend}
          changeLabel="vs yesterday"
          icon={<TrendingUp className="w-6 h-6 text-warning-600" />}
          iconBg="bg-warning-100"
        />
        <StatCard
          title="Staff On Clock"
          value={staff.length}
          icon={<Users className="w-6 h-6 text-slate-600" />}
          iconBg="bg-slate-100"
        />
      </div>

      {/* Procurement at a glance */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        <Link to="/ordering" className="card p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Items to Order</p>
              <p className="text-2xl font-bold text-slate-900">{procurement.itemsToOrder}</p>
              {procurement.criticalToOrder > 0 && (
                <p className="text-xs text-danger-600 font-medium">{procurement.criticalToOrder} critical</p>
              )}
            </div>
            <div className="p-2 rounded-lg bg-primary-100">
              <Package className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </Link>

        <Link to="/receiving" className="card p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Pending Deliveries</p>
              <p className="text-2xl font-bold text-slate-900">{procurement.pendingDeliveries}</p>
            </div>
            <div className="p-2 rounded-lg bg-warning-100">
              <Truck className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </Link>

        <Link to="/invoices" className="card p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Invoice Issues</p>
              <p className="text-2xl font-bold text-slate-900">{procurement.invoiceProblems}</p>
              {procurement.invoiceVarianceCents !== 0 && (
                <p className="text-xs text-danger-600 font-medium">
                  {formatCurrency(procurement.invoiceVarianceCents / 100)} variance
                </p>
              )}
            </div>
            <div className="p-2 rounded-lg bg-danger-100">
              <FileText className="w-6 h-6 text-danger-600" />
            </div>
          </div>
        </Link>

        <Link to="/food-cost" className="card p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Food Cost (wk)</p>
              <p className="text-2xl font-bold text-slate-900">
                {procurement.foodCostPercent !== null ? `${procurement.foodCostPercent}%` : '—'}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-success-100">
              <PieChart className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </Link>
      </div>

      {/* Charts and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Sales Chart */}
        <div className="lg:col-span-2 card p-4 md:p-6">
          <h2 className="text-lg font-semibold mb-4">Today's Sales</h2>
          <SalesChart data={data.hourlySales} />
        </div>

        {/* Table Status */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-4">Table Status</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-success-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-success-600" />
                <span className="font-medium">Available</span>
              </div>
              <span className="text-2xl font-bold text-success-600">
                {tables.available}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-primary-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-primary-600" />
                <span className="font-medium">Occupied</span>
              </div>
              <span className="text-2xl font-bold text-primary-600">
                {tables.occupied}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-warning-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-warning-600" />
                <span className="font-medium">Reserved</span>
              </div>
              <span className="text-2xl font-bold text-warning-600">
                {tables.reserved}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Orders and Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Recent Orders */}
        <div className="card">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold">Recent Orders</h2>
          </div>
          {orders.length === 0 ? (
            <div className="p-6 text-center text-slate-500">
              <ShoppingCart className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No orders yet today</p>
            </div>
          ) : (
          <div className="divide-y divide-slate-100">
            {orders.map((order: any) => (
              <div
                key={order.id}
                className="p-4 flex items-center justify-between"
              >
                <div>
                  <p className="font-medium">{order.tableName || `Table ${order.tableNumber}` || 'Order'}</p>
                  <p className="text-sm text-slate-500">{order.createdAt ? format(new Date(order.createdAt), 'h:mm a') : ''}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold">
                    {formatCurrency(order.totalCents || order.total || 0)}
                  </span>
                  <span
                    className={clsx(
                      'badge',
                      order.status === 'completed' && 'badge-success',
                      order.status === 'delivered' && 'badge-success',
                      order.status === 'in_progress' && 'badge-info',
                      order.status === 'cooking' && 'badge-info',
                      order.status === 'pending' && 'badge-warning',
                      order.status === 'new' && 'badge-warning'
                    )}
                  >
                    {(order.status || 'pending').replace('_', ' ')}
                  </span>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>

        {/* Alerts */}
        <div className="card">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold">Alerts & Notifications</h2>
          </div>
          {complianceStats?.alerts && complianceStats.alerts.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {complianceStats.alerts.map((alert: ComplianceAlert, index: number) => (
                <div
                  key={`${alert.type}-${index}`}
                  className={clsx(
                    'p-4 flex items-start gap-3',
                    alert.severity === 'critical' && 'bg-red-50',
                    alert.severity === 'warning' && 'bg-amber-50',
                    alert.severity === 'info' && 'bg-blue-50'
                  )}
                >
                  {alert.severity === 'critical' && (
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  {alert.severity === 'warning' && (
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  )}
                  {alert.severity === 'info' && (
                    <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={clsx(
                      'font-medium text-sm',
                      alert.severity === 'critical' && 'text-red-900',
                      alert.severity === 'warning' && 'text-amber-900',
                      alert.severity === 'info' && 'text-blue-900'
                    )}>
                      {alert.message}
                    </p>
                    <Link
                      to={/train|cert|card|allerg/i.test(String((alert as any).type || '') + alert.message) ? '/trainings'
                        : /stock|inventory|order/i.test(String((alert as any).type || '') + alert.message) ? '/inventory'
                        : '/staff'}
                      className={clsx(
                        'text-xs font-medium mt-1 inline-block',
                        alert.severity === 'critical' && 'text-red-700 hover:text-red-800',
                        alert.severity === 'warning' && 'text-amber-700 hover:text-amber-800',
                        alert.severity === 'info' && 'text-blue-700 hover:text-blue-800'
                      )}
                    >
                      View details →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-slate-500">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-success-400" />
              <p className="text-sm">No alerts at this time</p>
            </div>
          )}
        </div>
      </div>

      {/* Staff On Clock */}
      <div className="card">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold">Staff Currently On Clock</h2>
        </div>
        {staff.length === 0 ? (
          <div className="p-6 text-center text-slate-500">
            <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No staff currently clocked in</p>
          </div>
        ) : (
          <>
            {/* Mobile: Card layout */}
            <div className="md:hidden divide-y divide-slate-100">
              {staff.map((member: any) => {
                const clockIn = member.currentTimeEntry?.clockIn ? new Date(member.currentTimeEntry.clockIn) : null;
                const hoursWorked = clockIn ? Math.floor((Date.now() - clockIn.getTime()) / (1000 * 60 * 60)) : 0;
                const minutesWorked = clockIn ? Math.floor(((Date.now() - clockIn.getTime()) / (1000 * 60)) % 60) : 0;
                return (
                  <div key={member.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{member.firstName} {member.lastName}</p>
                      <p className="text-sm text-slate-500">{member.primaryRole || member.role}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-slate-900">{clockIn ? `${hoursWorked}h ${minutesWorked}m` : '-'}</p>
                      <p className="text-sm text-slate-500">{clockIn ? format(clockIn, 'h:mm a') : '-'}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Desktop: Table layout */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="table-header">Name</th>
                    <th className="table-header">Role</th>
                    <th className="table-header">Clocked In</th>
                    <th className="table-header">Hours Today</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {staff.map((member: any) => {
                    const clockIn = member.currentTimeEntry?.clockIn ? new Date(member.currentTimeEntry.clockIn) : null;
                    const hoursWorked = clockIn ? Math.floor((Date.now() - clockIn.getTime()) / (1000 * 60 * 60)) : 0;
                    const minutesWorked = clockIn ? Math.floor(((Date.now() - clockIn.getTime()) / (1000 * 60)) % 60) : 0;
                    return (
                      <tr key={member.id}>
                        <td className="table-cell font-medium">{member.firstName} {member.lastName}</td>
                        <td className="table-cell">{member.primaryRole || member.role}</td>
                        <td className="table-cell">{clockIn ? format(clockIn, 'h:mm a') : '-'}</td>
                        <td className="table-cell">{clockIn ? `${hoursWorked}h ${minutesWorked}m` : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// One calm, dismissible invitation to plug in the tools the restaurant already
// runs on. Hides itself once anything is connected, or forever on dismiss.
function ConnectToolsNudge() {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('mise_tools_nudge_dismissed') === '1');
  const { data: square } = useQuery({
    queryKey: ['square-status'],
    queryFn: async () => (await wholesaleApi.getSquareStatus()).data?.data || { connected: false },
    staleTime: 5 * 60_000,
  });
  const { data: seven } = useQuery({
    queryKey: ['sevenshifts-status'],
    queryFn: async () => (await wholesaleApi.getSevenShiftsStatus()).data?.data || { connected: false },
    staleTime: 5 * 60_000,
  });
  if (dismissed || square?.connected || seven?.connected) return null;
  return (
    <div className="card p-4 flex items-start justify-between gap-3 bg-primary-50 border-primary-100">
      <div>
        <p className="font-semibold text-slate-900 text-sm">Running on Square, 7shifts, Gmail or QuickBooks?</p>
        <p className="text-sm text-slate-600 mt-0.5">
          Connect them and your sales, schedules, order emails and books flow in on their own —{' '}
          <Link to="/settings?tab=integrations" className="text-primary-700 font-medium">two minutes each, in Settings → Integrations</Link>.
        </p>
      </div>
      <button
        onClick={() => { localStorage.setItem('mise_tools_nudge_dismissed', '1'); setDismissed(true); }}
        className="text-slate-400 hover:text-slate-600 text-sm shrink-0" aria-label="Dismiss">✕</button>
    </div>
  );
}
